import {IOptionsSelectionsValue, IRenderingConfig, ModelContext} from '../../types/app.types';
import {UrlModel} from './Url.model';
import {CatalogReferenceModel} from './CatalogReference.model';
import {ImageModel} from './Image.model';
// eslint-disable-next-line import/no-deprecated
import {RenderingConfigModel} from './RenderingConfig.model';
import {PriceModel} from './Price.model';
import {DiscountRuleModel} from './DiscountRule.model';
import {DescriptionLineModel} from './DescriptionLine.model';
import {LineItemWithPrivateFields} from './Cart.model';
import {DescriptionLine, PaymentOptionType} from '@wix/auto_sdk_ecom_current-cart';
import {ModifierGroupModel} from './ModifierGroup.model';
import {SPECS} from '../specs';

export class LineItemModel {
  public id: string;
  public sku: string;
  public quantity: number;
  public quantityAvailable: number;
  public itemType: string;
  public url: UrlModel;
  public productName: string;
  public catalogReference: CatalogReferenceModel;
  public price: PriceModel;
  public crossedOutPrice: PriceModel;
  public crossedOutPriceByQuantity: PriceModel;
  public baseUnitPrice: PriceModel;
  public lineItemPrice: PriceModel;
  public image: ImageModel;
  public descriptionLines: DescriptionLineModel[];
  public modifierGroups: ModifierGroupModel[];
  /**
   * @deprecated
   */
  // eslint-disable-next-line import/no-deprecated
  public renderingConfig: RenderingConfigModel;
  public paymentOption: PaymentOptionType;
  public savePaymentMethod: boolean;
  public depositAmount: PriceModel;
  public modifiersTotalPrice: PriceModel;
  public discountRules: DiscountRuleModel[];
  public priceUndetermined: boolean;
  public fixedQuantity: boolean;

  constructor(params: LineItemModel) {
    this.id = params.id;
    this.sku = params.sku;
    this.quantity = params.quantity;
    this.quantityAvailable = params.quantityAvailable;
    this.itemType = params.itemType;
    this.url = params.url;
    this.productName = params.productName;
    this.catalogReference = params.catalogReference;
    this.price = params.price;
    this.crossedOutPrice = params.crossedOutPrice;
    this.crossedOutPriceByQuantity = params.crossedOutPriceByQuantity;
    this.baseUnitPrice = params.baseUnitPrice;
    this.lineItemPrice = params.lineItemPrice;
    this.image = params.image;
    this.descriptionLines = params.descriptionLines;
    this.modifierGroups = params.modifierGroups;
    // eslint-disable-next-line import/no-deprecated
    this.renderingConfig = params.renderingConfig;
    this.paymentOption = params.paymentOption;
    this.savePaymentMethod = params.savePaymentMethod;
    this.depositAmount = params.depositAmount;
    this.modifiersTotalPrice = params.modifiersTotalPrice;
    this.discountRules = params.discountRules;
    this.fixedQuantity = params.fixedQuantity;
    this.priceUndetermined = params.priceUndetermined;
  }

  public static fromSDK(
    lineItem: LineItemWithPrivateFields,
    discountRulesNames: string[] = [],
    modelContext: ModelContext,
    renderingConfig?: IRenderingConfig,
    bookingsOverrideOptions?: IOptionsSelectionsValue[]
  ) {
    const crossedOutPrice = getCrossedOutPrice(lineItem);
    const modeledCrossedOutPrice = crossedOutPrice ? PriceModel.fromSDK(crossedOutPrice, modelContext) : undefined;
    const modeledPrice = PriceModel.fromSDK(lineItem?.price, modelContext);
    const modeledModifiersTotalPrice = PriceModel.fromSDK(lineItem.modifiersTotalPrice, modelContext);
    const quantity = lineItem.quantity;
    return new LineItemModel({
      // eslint-disable-next-line import/no-deprecated
      id: lineItem._id,
      sku: lineItem.physicalProperties?.sku,
      quantity,
      quantityAvailable: lineItem.availability?.quantityAvailable,
      itemType:
        lineItem.itemType?.preset?.toLowerCase() ?? /* istanbul ignore next */ lineItem.itemType?.custom?.toLowerCase(),
      url: UrlModel.fromSDK(lineItem.url),
      productName: lineItem.productName.translated,
      catalogReference: CatalogReferenceModel.fromSDK(lineItem?.catalogReference),
      price: modeledPrice,
      crossedOutPrice: modeledCrossedOutPrice,
      crossedOutPriceByQuantity: crossedOutPrice
        ? PriceModel.fromSDK(
            crossedOutPriceByQuantity({
              modeledCrossedOutPrice,
              quantity,
              modelContext,
            }),
            modelContext
          )
        : undefined,
      baseUnitPrice: PriceModel.fromSDK(
        getBaseUnitPrice({
          modeledPrice,
          modeledCrossedOutPrice,
          modeledModifiersTotalPrice,
          modelContext,
        }),
        modelContext
      ),
      lineItemPrice: PriceModel.fromSDK(lineItem?.lineItemPrice, modelContext),
      image: lineItem?.image ? ImageModel.fromSDK(lineItem.image) : /* istanbul ignore next */ undefined,
      descriptionLines: bookingsOverrideOptions
        ? (bookingsOverrideOptions ?? /* istanbul ignore next */ []).map((option: IOptionsSelectionsValue) =>
            DescriptionLineModel.fromGQL(option.title, option.value)
          )
        : (lineItem?.descriptionLines ?? /* istanbul ignore next */ []).map((descriptionLine: DescriptionLine) =>
            DescriptionLineModel.fromSDK(descriptionLine)
          ),
      modifierGroups: (lineItem.modifierGroups ?? []).map((modifierGroup) =>
        ModifierGroupModel.fromSDK(modifierGroup, modelContext)
      ),
      paymentOption: lineItem.paymentOption as PaymentOptionType,
      savePaymentMethod: lineItem.savePaymentMethod,
      depositAmount: lineItem?.depositAmount ? PriceModel.fromSDK(lineItem.depositAmount, modelContext) : undefined,
      modifiersTotalPrice: modeledModifiersTotalPrice,
      discountRules: discountRulesNames.map((discountRuleName) => DiscountRuleModel.fromSDK(discountRuleName)),
      renderingConfig:
        !modelContext.viewerContext.isExperimentEnabled(SPECS.DontUseLineItemEnricherInCartAndCheckout) &&
        renderingConfig
          ? // eslint-disable-next-line import/no-deprecated
            RenderingConfigModel.fromGQL(renderingConfig)
          : undefined,
      priceUndetermined: modelContext.viewerContext.isExperimentEnabled(SPECS.DontUseLineItemEnricherInCartAndCheckout)
        ? lineItem.priceUndetermined
        : false,
      fixedQuantity: modelContext.viewerContext.isExperimentEnabled(SPECS.DontUseLineItemEnricherInCartAndCheckout)
        ? lineItem.fixedQuantity
        : false,
    });
  }
}

function getCrossedOutPrice(lineItem: LineItemWithPrivateFields) {
  if (lineItem.priceBeforeDiscounts?.formattedConvertedAmount !== lineItem.price?.formattedConvertedAmount) {
    return lineItem.priceBeforeDiscounts;
  } else if (lineItem.fullPrice?.formattedConvertedAmount !== lineItem.price?.formattedConvertedAmount) {
    return lineItem.fullPrice;
  }
  return undefined;
}

function getBaseUnitPrice({
  modeledPrice,
  modeledCrossedOutPrice,
  modeledModifiersTotalPrice,
  modelContext,
}: {
  modeledPrice: PriceModel;
  modeledCrossedOutPrice: PriceModel;
  modeledModifiersTotalPrice?: PriceModel;
  modelContext: ModelContext;
}) {
  if (modeledModifiersTotalPrice.convertedAmount === 0) {
    return {
      convertedAmount: modeledPrice.convertedAmount.toString(),
      formattedConvertedAmount: modeledPrice.formattedConvertedAmount,
    };
  }

  const unitPriceAmount = modeledCrossedOutPrice?.convertedAmount
    ? modeledCrossedOutPrice?.convertedAmount
    : modeledPrice.convertedAmount;

  const baseUnitPriceAmount = unitPriceAmount - modeledModifiersTotalPrice.convertedAmount;

  return {
    convertedAmount: baseUnitPriceAmount.toString(),
    formattedConvertedAmount: modelContext.formatAmount(baseUnitPriceAmount),
  };
}

function crossedOutPriceByQuantity({
  modeledCrossedOutPrice,
  quantity,
  modelContext,
}: {
  modeledCrossedOutPrice: PriceModel;
  quantity: number;
  modelContext: ModelContext;
}) {
  return {
    convertedAmount: (modeledCrossedOutPrice.convertedAmount * quantity).toString(),
    formattedConvertedAmount: modelContext.formatAmount(modeledCrossedOutPrice.convertedAmount * quantity),
  };
}
